/**
 * Costura de alto nivel: do convite ate a sessao pronta para trocar quadros.
 *
 * E aqui que a UI toca — as camadas de baixo (x3dh, ratchet, frame) ficam
 * disponiveis para teste e auditoria, mas o app usa este arquivo.
 */
import type { Identity } from './identity.js'
import type { PreKeyBundle, PreKeyStore } from './prekeys.js'
import { RatchetSession } from './ratchet.js'
import { deriveSafetyCode, type SafetyCode } from './safety-code.js'
import { initiate, respond, type X3dhInitialMessage } from './x3dh.js'

export const ContactTrust = {
  NaoVerificado: 'NAO_VERIFICADO',
  Verificado: 'VERIFICADO',
} as const
export type ContactTrust = (typeof ContactTrust)[keyof typeof ContactTrust]

export interface Session {
  readonly ratchet: RatchetSession
  /** AD fixo da sessao: amarra cada mensagem as duas identidades. */
  readonly associatedData: Uint8Array
  readonly safetyCode: SafetyCode
}

export interface StartedSession extends Session {
  /** Vai junto da primeira mensagem, para o outro lado refazer o X3DH. */
  readonly initialMessage: X3dhInitialMessage
}

/**
 * Lado de quem inicia, com o bundle que o Worker devolveu.
 *
 * `peerFingerprint` vem do convite, nao do servidor. E ele que amarra o
 * bundle a pessoa certa — ver FingerprintMismatchError.
 */
export function startSession(
  self: Identity,
  peerBundle: PreKeyBundle,
  peerFingerprint: Uint8Array,
): StartedSession {
  const agreed = initiate(self, peerBundle, peerFingerprint)
  return {
    ratchet: RatchetSession.initiator({
      sharedSecret: agreed.sharedSecret,
      remoteRatchetPublicKey: agreed.remoteRatchetPublicKey,
      initiatorHeaderKey: agreed.initiatorHeaderKey,
      responderHeaderKey: agreed.responderHeaderKey,
    }),
    associatedData: agreed.associatedData,
    safetyCode: deriveSafetyCode(self.fingerprint, peerFingerprint),
    initialMessage: agreed.initialMessage,
  }
}

/** Lado de quem responde, ao receber a primeira mensagem. */
export function acceptSession(
  self: Identity,
  preKeys: PreKeyStore,
  initialMessage: X3dhInitialMessage,
  peerFingerprint: Uint8Array,
): Session {
  // Capturado ANTES do respond(), que consome a OTPK do store.
  const ownRatchetKeyPair = {
    publicKey: preKeys.signedPreKey.publicKey,
    privateKey: preKeys.signedPreKey.privateKey,
  }
  const agreed = respond(self, preKeys, initialMessage)
  return {
    ratchet: RatchetSession.responder({
      sharedSecret: agreed.sharedSecret,
      ownRatchetKeyPair,
      initiatorHeaderKey: agreed.initiatorHeaderKey,
      responderHeaderKey: agreed.responderHeaderKey,
    }),
    associatedData: agreed.associatedData,
    safetyCode: deriveSafetyCode(self.fingerprint, peerFingerprint),
  }
}
